import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore, selectUser, canAccessSite, hasNoSiteAccess } from "@safers/auth";
import { useIsMobile } from "@/hooks/useIsMobile";
import { NoSiteAccess } from "./components/NoSiteAccess";
import { DashboardLayout } from "./DashboardLayout";
import { LeftPanel } from "./components/LeftPanel";
import { RightPanel } from "./components/RightPanel";
import { KoreaMap, type POI } from "./components/KoreaMap";
import { MobileHome } from "./components/MobileHome";
import { sitesService, eventsService, type Site } from "@/services";
import { STATUS_COLORS } from "@/styles/tokens";
import { buildSiteStatusMap } from "./utils";

const DEFAULT_POI_COLOR = STATUS_COLORS.brand;
const WARNING_POI_COLOR = STATUS_COLORS.warning;
const DANGER_POI_COLOR = STATUS_COLORS.danger;

function siteToPOI(site: Site, siteStatusMap: Map<number, "warning" | "danger">): POI | null {
  // #11 마이그레이션 이후 Site는 lon/lat 직접 필드 보유 (옛 longitude/latitude/location WKT는 제거됨)
  if (site.lon == null || site.lat == null) return null;

  const status = siteStatusMap.get(site.id);
  const color =
    status === "danger"
      ? DANGER_POI_COLOR
      : status === "warning"
        ? WARNING_POI_COLOR
        : DEFAULT_POI_COLOR;

  return {
    id: String(site.id),
    longitude: site.lon,
    latitude: site.lat,
    color,
    size: 1.5,
    data: { name: site.name, region: site.region, status },
  };
}

export function DashboardPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const user = useAuthStore(selectUser);
  const [pois, setPois] = useState<POI[]>([]);

  useEffect(() => {
    if (isMobile) return; // 모바일에서는 대시보드 데이터 불필요
    async function fetchData() {
      const [sitesResult, eventsResult] = await Promise.allSettled([
        sitesService.getSites({ size: 1000 }),
        // LeftPanel 과 동일 size — POI 상태 색상이 패널 집계와 어긋나지 않도록
        eventsService.getEvents({ page: 1, size: 1000 }),
      ]);

      const allSites = sitesResult.status === "fulfilled" ? sitesResult.value.data.content : [];
      const sites = allSites.filter((s) => canAccessSite(user, s.id));
      const events = eventsResult.status === "fulfilled" ? eventsResult.value.data.content : [];

      const siteStatusMap = buildSiteStatusMap(events);
      const sitePOIs = sites
        .map((site) => siteToPOI(site, siteStatusMap))
        .filter((poi): poi is POI => poi !== null);
      setPois(sitePOIs);
    }
    fetchData();
    // user 전체를 deps 로 둔다 — 권한 게이트라 권한 변경 시 재필터링이 필수.
    // user.id 로 좁히면 같은 사용자의 권한만 바뀔 때 갱신을 놓친다(보안 회귀).
  }, [isMobile, user]);

  if (hasNoSiteAccess(user)) {
    return <NoSiteAccess />;
  }

  if (isMobile) {
    return <MobileHome />;
  }

  const handlePOISiteClick = (poi: POI) => {
    navigate(`/site/${poi.id}`);
  };

  const handlePOICctvAIClick = (poi: POI) => {
    navigate(`/cctv-ai/${poi.id}`);
  };

  return (
    <DashboardLayout leftPanel={<LeftPanel />} rightPanel={<RightPanel />}>
      <KoreaMap
        className="w-full h-full"
        pois={pois}
        onPOISiteClick={handlePOISiteClick}
        onPOICctvAIClick={handlePOICctvAIClick}
      />
    </DashboardLayout>
  );
}
